import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, ProgressBar } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import HeaderNavbar from '../components/HeaderNavbar';
import Footer from '../components/Footer';
import Spinner from 'react-bootstrap/Spinner';
import config from '../config';
import api from '../lib/request';
import { evaluatePasswordStrength } from '../lib/passwordRules';
import './Login.css';
import { countries, codeToFlag } from '../data/countries';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const initialFormState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  country: ''
};

const Register = () => {
  const query = new URLSearchParams(useLocation().search);
  const goback = query.get('goback');
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [reasons, setReasons] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [completed, setCompleted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const errorRef = useRef(null);

  const passwordStrength = useMemo(() => evaluatePasswordStrength(form.password), [form.password]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validateForm = () => {
    const issues = [];

    if (!form.firstName.trim()) issues.push('First name is required.');
    if (!form.lastName.trim()) issues.push('Last name is required.');
    if (!form.country.trim()) issues.push('Country is required.');

    const trimmedEmail = form.email.trim();
    if (!trimmedEmail) {
      issues.push('Email is required.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      issues.push('Enter a valid email address.');
    }

    if (!form.password) {
      issues.push('Password is required.');
    } else if (!passwordStrength.isStrong) {
      issues.push('Password must meet all strength requirements.');
    }

    if (form.password !== form.confirmPassword) {
      issues.push('Passwords do not match.');
    }

    return issues;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setReasons([]);
    setSuccessMessage('');
    setCompleted(false);

    const issues = validateForm();
    if (issues.length) {
      setError('Please fix the highlighted issues and try again.');
      setReasons(issues);
      return;
    }

    const captchaSiteKey = config.hcaptchaSiteKey;
    const captchaEnabled = !!captchaSiteKey;
    if (captchaEnabled && !captchaToken) {
      setError('Please complete the captcha.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        countryCode: form.country.trim(),
        email: form.email.trim(),
        password: form.password,
        // include captcha token when enabled
        ...(captchaEnabled ? { hcaptchaToken: captchaToken } : {})
      };
      const response = await api.post('/api/v1/auth/register', payload);
      const message = response?.message || 'Registration received. Check your email to verify your account.';
      setSuccessMessage(message);
      setForm(initialFormState);
      setCompleted(true);
      // reset captcha state
      try { captchaRef.current?.resetCaptcha(); } catch (e) {}
      setCaptchaToken('');
    } catch (err) {
      const backendReasons = Array.isArray(err?.reasons) ? err.reasons : [];
      if (backendReasons.length) {
        setReasons(backendReasons);
      }
      setError(err?.message || 'Unable to complete registration. Please try again later.');
      setCompleted(false);
      // If captcha was used, reset it so user can submit again with a fresh token
      try { captchaRef.current?.resetCaptcha(); } catch (e) {}
      setCaptchaToken('');
    } finally {
      setSubmitting(false);
    }
  };

  // When an error or reasons are present, scroll/focus to the error area
  useEffect(() => {
    if ((error || (reasons && reasons.length > 0)) && errorRef.current) {
      try {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        if (typeof errorRef.current.focus === 'function') {
          errorRef.current.focus();
        }
      } catch (e) {}
    }
  }, [error, reasons]);

  return (
    <>
      <HeaderNavbar />
      <div className="row">
        <div className="Auth-form-container">
          {completed ? (
            <div className="Auth-form auth-account-form">
              <div className="Auth-form-content" style={{ textAlign: 'center' }}>
                <div style={{display: 'flex', justifyContent: 'center', marginBottom: 10}}>
                  <img src="/logo.svg" alt="Logo" width="100" height="100" />
                </div>
                {successMessage && <Alert variant="success">{successMessage}</Alert>}
                <div className="text-center mt-2">
                  <Link to={`${config.prefixPath}/login${goback ? `?goback=${encodeURIComponent(goback)}&rl=1` : ''}`} className="btn btn-link">Log in</Link>
                </div>
              </div>
            </div>
          ) : (
            <form className="Auth-form auth-account-form" onSubmit={handleSubmit} noValidate>
              <div className="Auth-form-content">
                <div style={{display: 'flex', justifyContent: 'center', marginBottom: 10}}>
                  <img src="/logo.svg" alt="Logo" width="100" height="100" />
                </div>
                <h3 className="Auth-form-title">Create your account</h3>
                <div className="text-center">
                  Already registered? <Link to={`${config.prefixPath}/login${goback ? `?goback=${encodeURIComponent(goback)}&rl=1` : ''}`}>Log in</Link>
                </div>

                <div ref={errorRef} tabIndex={-1} aria-live="polite">
                  {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                  {reasons.length > 0 && (
                    <Alert variant="danger" className="mt-3">
                      <ul className="mb-0" style={{ paddingLeft: '1.2rem' }}>
                        {reasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </div>

                <div className="form-group mt-3">
                  <label>First name</label>
                  <input
                    type="text"
                    className="form-control mt-1"
                    placeholder="Enter first name"
                    value={form.firstName}
                    onChange={handleChange('firstName')}
                    required
                    autoComplete="given-name"
                  />
                </div>

                <div className="form-group mt-3">
                  <label>Last name</label>
                  <input
                    type="text"
                    className="form-control mt-1"
                    placeholder="Enter last name"
                    value={form.lastName}
                    onChange={handleChange('lastName')}
                    required
                    autoComplete="family-name"
                  />
                </div>

                <div className="form-group mt-3">
                  <label>Email address</label>
                  <input
                    type="email"
                    className="form-control mt-1"
                    placeholder="Enter email"
                    value={form.email}
                    onChange={handleChange('email')}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="form-group mt-3">
                  <label>Country</label>
                  <select
                    className="form-control mt-1"
                    value={form.country}
                    onChange={handleChange('country')}
                    required
                  >
                    <option value="" disabled>Select country</option>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {codeToFlag(c.code)} {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group mt-3">
                  <label>Password</label>
                  <input
                    type="password"
                    className="form-control mt-1"
                    placeholder="Create a password"
                    value={form.password}
                    onChange={handleChange('password')}
                    required
                    autoComplete="new-password"
                  />
                  <div className="mt-2">
                    <ProgressBar now={passwordStrength.percent} variant={passwordStrength.variant} />
                    <small className="d-block mt-1">{passwordStrength.label}</small>
                    <ul className="password-requirements mt-2 mb-0" style={{ listStyle: 'none', paddingLeft: 0 }}>
                      {passwordStrength.statuses.map((req) => (
                        <li key={req.id} className={req.met ? 'text-success' : 'text-muted'}>
                          {req.met ? '[x]' : '[ ]'} {req.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="form-group mt-3">
                  <label>Confirm password</label>
                  <input
                    type="password"
                    className="form-control mt-1"
                    placeholder="Confirm password"
                    value={form.confirmPassword}
                    onChange={handleChange('confirmPassword')}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group mt-3">
                  {config.hcaptchaSiteKey ? (
                    <HCaptcha
                      sitekey={config.hcaptchaSiteKey}
                      onVerify={(token /*, ekey*/) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken('')}
                      onError={() => setCaptchaToken('')}
                      ref={captchaRef}
                      reCaptchaCompat={false}
                    />
                  ) : (
                    <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                      Captcha is disabled. Set REACT_APP_HCAPTCHA_SITEKEY to enable.
                    </div>
                  )}
                </div>

                <div className="d-grid gap-2 mt-3">
                  {submitting ? (
                    <Spinner animation="border" />
                  ) : (
                    <button type="submit" className="btn btn-primary">Create account</button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Register;
